import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Alert, Box, Button, Modal, Text } from '@nimbus-ds/components';
import { Country } from 'App/i18n/enums';
import { ROUTES } from 'App/Routes/constants';
import { ProofOfLifePendingModal } from 'commons/components';
import { StoreFlag, StoreStatusReason } from 'commons/enums';
import {
  PermissionFeature,
  useBalance,
  useBankAccount,
  useCountry,
  useFeatureFlag,
  useIdentityValidation,
  useIsMobile,
  usePermission,
  useProofOfLifeUrl,
  useProviderWithdrawalDisabled,
  useStoreFlag,
  useStoreInfo,
} from 'commons/hooks';
import { useGetWithdrawalsPreferences } from 'commons/hooks';
import { configurationService, FeatureFlag } from 'commons/services';
import { ValidationStatusType } from 'commons/types';
import { useTranslationWithPrefix } from 'commons/utils';
import AutomaticWithdrawalsModal from '../Mexico/components/AutomaticWithdrawalsModal';
import PendingPaymentsForChargebackModal from '../Mexico/components/PendingPaymentsForChargebackModal';
import AvailableToWithdrawCard from './AvailableToWithdrawCard';
import { FutureIncomesCard } from './FutureIncomesCard';
import HomeCardsSkeleton from './HomeCardsSkeleton';

interface stateType {
  showLoadingDelay: boolean;
}

function HomeCards(): JSX.Element {
  const { t } = useTranslationWithPrefix('home');
  const { t: tTransfer } = useTranslationWithPrefix('transfer');

  const location = useLocation<stateType>();
  const history = useHistory();

  const { storeInfo } = useStoreInfo();

  const thereArePendingPaymentsForChargeback =
    storeInfo.country === Country.MEXICO &&
    storeInfo.statusReason === StoreStatusReason.PENDING_DEBT;

  const bankAccount = useBankAccount();
  const isMobile = useIsMobile();

  const [isTransfering, setIsTransfering] = useState(false);
  const [showErrorAlert, setShowAlertState] = useState(false);
  const [showLoadingDelay, setShowLoadingDelay] = useState(
    location.state?.showLoadingDelay ?? false,
  );
  const [showProofOfLifePendingModal, setShowProofOfLifePendingModal] =
    useState(false);
  const [showAutomaticWithdrawalsModal, setShowAutomaticWithdrawalsModal] =
    useState(false);
  const [
    showPendingPaymentsForChargebackModal,
    setShowPendingPaymentsForChargebackModal,
  ] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState({
    title: '',
    open: false,
    children: <></>,
  });

  const identityValidation = useIdentityValidation();
  const { proofOfLifeUrl } = useProofOfLifeUrl({
    enabled: showProofOfLifePendingModal,
  });
  const { data: withdrawalsPreferences, isAutomatedWithdrawEnabled } =
    useGetWithdrawalsPreferences();

  const withdrawPermission = usePermission(PermissionFeature.Withdraw);
  const { storeCountry } = useCountry();

  const { enabled: globalTransferDisabled } = useFeatureFlag(
    FeatureFlag.FS_PAGONUBE_TRANSFER_DISABLED,
  );
  const { disabled: providerTransferDisabled } =
    useProviderWithdrawalDisabled();
  const transferDisabled = globalTransferDisabled || providerTransferDisabled;

  const { enabled: partialWithdrawEnabled } = useFeatureFlag(
    FeatureFlag.FS_PAGONUBE_PARTIAL_WITHDRAW_ENABLED,
  );

  const { enabled: transferDisabledByLending } = useStoreFlag(
    StoreFlag.WithdrawBlockedByLending,
  );

  const transferDisableReason = useMemo(() => {
    const message = transferDisabledByLending
      ? tTransfer('transferDisabledReason.lending')
      : undefined;

    return message;
  }, [transferDisabledByLending, tTransfer]);

  const MAX_AGRESSIVE_REFETCH_COUNT = 15;
  const DEFAULT_REFETCH_INTERVAL_MS = 60 * 1000;
  const AGRESSIVE_REFETCH_INTERVAL_MS = 1000;

  const {
    isLoading: balanceLoading,
    isError: balanceError,
    waitingFunds,
    availableToWithdraw,
  } = useBalance(
    {
      refetchInterval: (data, query) => {
        const hasPending = data?.hasPendingTransfers ?? false;
        if (!hasPending) {
          return DEFAULT_REFETCH_INTERVAL_MS;
        }

        if (query.state.dataUpdateCount > MAX_AGRESSIVE_REFETCH_COUNT) {
          return DEFAULT_REFETCH_INTERVAL_MS;
        }

        return AGRESSIVE_REFETCH_INTERVAL_MS;
      },
      retry: 0,
      onSuccess: (balance) => {
        setShowLoadingDelay(balance.hasPendingTransfers ?? false);
      },
    },
    true,
  );

  const isError = balanceError || withdrawPermission.isError;

  const redirectBankAccountRegistration = () => {
    history.push(ROUTES.BANK_ACCOUNT_CONFIG);
  };

  const renderValidationModal = (status: ValidationStatusType) => {
    switch (status) {
      case 'pending':
      case 'blocked':
        setShowValidationModal({
          title: t('limitModal.pending.title'),
          open: true,
          children: (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="stretch"
              gap="6"
            >
              <Text>{t('limitModal.pending.firstPar')}</Text>

              <Text>{t('limitModal.pending.secondPar')}</Text>
              <Box display="flex" justifyContent="flex-end" gap="4">
                <Button onClick={validateIdentity} appearance="primary">
                  {t('limitModal.pending.validateId')}
                </Button>
              </Box>
            </Box>
          ),
        });
        break;

      case 'pending_review':
      case 'pending_documentation':
        setShowValidationModal({
          title: t('limitModal.pendingReview.title'),
          open: true,
          children: <Text>{t('limitModal.pendingReview.firstPar')}</Text>,
        });
        break;

      default:
        break;
    }
  };

  const closeValidationModal = () => {
    setShowValidationModal({
      title: '',
      open: false,
      children: <></>,
    });
  };

  const transferMoneyMx = () => {
    if (thereArePendingPaymentsForChargeback) {
      setShowPendingPaymentsForChargebackModal(true);
      return;
    }
    if (isAutomatedWithdrawEnabled) {
      setShowAutomaticWithdrawalsModal(true);
      return;
    }
    setIsTransfering(true);
    if (bankAccount.isError || !bankAccount.data?.fiscalDocument) {
      setShowAlertState(true);
    } else if (!bankAccount.data?.isRegistered) {
      redirectBankAccountRegistration();
    } else {
      history.push(ROUTES.TRANSFER);
    }
    setIsTransfering(false);
  };

  const transferMoney = () => {
    setIsTransfering(true);

    // TODO: change getIdValidation by useIdentityValidation
    configurationService
      .getIdValidation()
      .then((response) => {
        if (response.status == 'active' || response.status == 'denied') {
          if (response.status == 'active' && response.isProofOfLifePending) {
            setShowProofOfLifePendingModal(true);
          } else if (bankAccount.isError || !bankAccount.data?.fiscalDocument) {
            setShowAlertState(true);
          } else if (!bankAccount.data?.isRegistered) {
            redirectBankAccountRegistration();
          } else {
            history.push(ROUTES.TRANSFER);
          }
        } else {
          renderValidationModal(response.status);
        }
      })
      .catch(() => setShowAlertState(true))
      .finally(() => setIsTransfering(false));
  };

  function validateIdentity() {
    history.push(ROUTES.IDENTITY_VALIDATION);
  }

  const accountIsDenied = identityValidation.data?.status === 'denied';

  const disableTransfer =
    availableToWithdraw.value <= 0 ||
    isTransfering ||
    bankAccount.isLoading ||
    !withdrawPermission.data?.permitted ||
    transferDisabled ||
    accountIsDenied;

  const showSupportTooltip =
    accountIsDenied && storeCountry === Country.ARGENTINA;

  const handleTransferClickMapper: Record<Country, () => void> = {
    [Country.ARGENTINA]: () => transferMoney(),
    [Country.BRAZIL]: () => transferMoney(),
    [Country.MEXICO]: () => transferMoneyMx(),
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="stretch" gap="4">
      <Alert
        appearance="danger"
        show={showErrorAlert}
        title={t('alert.errorTitle')}
        onRemove={() => setShowAlertState(false)}
      >
        {t('alert.errorDescription')}
      </Alert>
      <Modal open={showValidationModal.open} onDismiss={closeValidationModal}>
        <Modal.Header title={showValidationModal.title} />
        {showValidationModal.children}
      </Modal>
      {showAutomaticWithdrawalsModal && (
        <AutomaticWithdrawalsModal
          open={showAutomaticWithdrawalsModal}
          onDismiss={() => setShowAutomaticWithdrawalsModal(false)}
          withdrawalInterval={withdrawalsPreferences?.interval ?? 'daily'}
          dayOfTheWeek={withdrawalsPreferences?.anchor}
        />
      )}
      {showPendingPaymentsForChargebackModal && (
        <PendingPaymentsForChargebackModal
          open={showPendingPaymentsForChargebackModal}
          onDismiss={() => setShowPendingPaymentsForChargebackModal(false)}
        />
      )}
      <ProofOfLifePendingModal
        onDismiss={() => setShowProofOfLifePendingModal(false)}
        open={showProofOfLifePendingModal}
        proofOfLifeUrl={proofOfLifeUrl}
      />
      <Box>
        <Box
          display="grid"
          gridGap="4"
          gridTemplateColumns={isMobile ? '1fr' : '1fr 1fr'}
        >
          <AvailableToWithdrawCard
            availableToWithdraw={availableToWithdraw}
            loading={balanceLoading || identityValidation.isLoading}
            showLoadingDelay={showLoadingDelay}
            isError={isError}
            showSupportTooltip={showSupportTooltip}
            transferFlagDisabled={transferDisabled}
            handleTransferClick={handleTransferClickMapper[storeCountry]}
            disabled={disableTransfer}
            automatedWithdrawEnabled={isAutomatedWithdrawEnabled}
            thereArePendingPayments={thereArePendingPaymentsForChargeback}
            partialWithdrawEnabled={partialWithdrawEnabled}
            transferDisableReason={transferDisableReason}
          />
          <FutureIncomesCard
            amount={waitingFunds}
            skeleton={balanceLoading}
            showLoadingDelay={showLoadingDelay}
            hasError={isError}
          />
        </Box>
      </Box>
    </Box>
  );
}

HomeCards.Skeleton = HomeCardsSkeleton;
export default HomeCards;
